import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { StatusBadge } from "@/components/StatusBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, type BrainVersion, type ReleaseChannel } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ModelSettingsSection } from "@/pages/admin/ModelSettingsSection";
import { SandboxesPanel } from "@/pages/admin/SandboxesPanel";
import { SandboxProviderSection } from "@/pages/admin/SandboxProviderSection";
import { BundleRailwayTokenSection } from "@/pages/admin/BundleRailwayTokenSection";
import { SelfHostedSection } from "@/pages/admin/SelfHostedBoo";
import { SELF_HOSTED } from "@/lib/self-hosted";
import { VercelSandboxesPanel } from "@/pages/admin/VercelSandboxesPanel";
import { ContainerFeedbackPanel } from "@/pages/admin/ContainerFeedbackPanel";
import { Bundles } from "@/pages/container-tabs/integrations/Bundles";
import { relativeIso, untilIso } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SetupChecklist } from "@/components/SetupChecklist";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink, RefreshCw, Rocket, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// ── Presentational primitives ─────────────────────────────────────────

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start py-1.5">
      <div className="text-xs text-muted-foreground pt-0.5">{label}</div>
      <div className={`text-sm break-all ${mono ? "font-mono text-xs" : ""}`}>
        {value === null || value === undefined || value === "" ? (
          <span className="text-muted-foreground italic">—</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

// A titled card for grouping fields within a tab.
function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card">
      <div className="flex min-h-11 items-center justify-between gap-3 border-b px-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
        {action}
      </div>
      <div className="px-4 py-2">{children}</div>
    </section>
  );
}

function KindBadge({ kind }: { kind: BrainVersion["kind"] }) {
  if (kind === "release") {
    return <Badge variant="secondary" className="text-[10px]">release</Badge>;
  }
  if (kind === "rolling") {
    return <Badge variant="outline" className="text-[10px]">rolling</Badge>;
  }
  return (
    <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-600">
      staging
    </Badge>
  );
}

const CHANNEL_OPTIONS: { value: ReleaseChannel; label: string; blurb: string }[] = [
  { value: "stable", label: "Stable", blurb: "Follows the newest release (what :latest points at). Rolled out hourly." },
  { value: "edge", label: "Edge", blurb: "Follows the newest build of main. Rolled out hourly; for testing." },
  { value: "pinned", label: "Pinned", blurb: "Stays on whatever was deployed by hand. Set automatically by Deploy." },
];

function ChannelOption({
  option,
  selected,
}: {
  option: (typeof CHANNEL_OPTIONS)[number];
  selected: boolean;
}) {
  return (
    <label
      htmlFor={`chan-${option.value}`}
      className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors hover:bg-muted/40 ${
        selected ? "border-primary bg-muted/30" : ""
      }`}
    >
      <RadioGroupItem value={option.value} id={`chan-${option.value}`} className="mt-0.5" />
      <div className="space-y-0.5 min-w-0">
        <div className="text-xs font-medium">{option.label}</div>
        <p className="text-[11px] text-muted-foreground">{option.blurb}</p>
      </div>
    </label>
  );
}

function VersionOption({
  version,
  selected,
  isCurrent,
}: {
  version: BrainVersion;
  selected: boolean;
  isCurrent: boolean;
}) {
  return (
    <label
      htmlFor={`ver-${version.tag}`}
      className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors hover:bg-muted/40 ${
        selected ? "border-primary bg-muted/30" : ""
      }`}
    >
      <RadioGroupItem value={version.tag} id={`ver-${version.tag}`} className="mt-0.5" />
      <div className="space-y-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs font-medium">{version.tag}</span>
          <KindBadge kind={version.kind} />
          {isCurrent && <Badge className="text-[10px]">current</Badge>}
          {version.resolvesTo && (
            <span className="text-[11px] text-muted-foreground">→ {version.resolvesTo}</span>
          )}
        </div>
        {version.highlights.length > 0 && (
          <ul className="text-[11px] text-muted-foreground list-disc pl-4 space-y-0.5">
            {version.highlights.slice(0, 3).map((h, i) => (
              <li key={`${version.tag}-${i}`}>{h}</li>
            ))}
          </ul>
        )}
        {version.notesUrl && (
          <a
            href={version.notesUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[11px] text-primary underline underline-offset-2 inline-flex items-center gap-1"
          >
            Release notes <ExternalLink className="size-3" />
          </a>
        )}
      </div>
    </label>
  );
}

// ── Page ───────────────────────────────────────────────────────────────

export function AdminContainerDetailPage() {
  const { containerId } = useParams({ strict: false }) as { containerId: string };
  const { tab, bundles } = useSearch({ strict: false }) as { tab?: string; bundles?: boolean };
  const showBundles = !!bundles;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-container", containerId],
    queryFn: () => api.admin.container(containerId),
    enabled: !!containerId,
    // Domain re-provisioning is fire-and-forget on the backend; keep the page
    // live while it walks pending → verifying → active/error.
    refetchInterval: (query) => {
      const status = query.state.data?.customDomainStatus;
      return status === "pending" || status === "verifying" ? 5_000 : false;
    },
  });

  const reprovisionDomain = useMutation({
    mutationFn: () => api.admin.reprovisionCustomDomain(containerId),
    onSuccess: () => {
      toast.success("Re-provisioning domain — this can take a minute or two");
      queryClient.invalidateQueries({ queryKey: ["admin-container", containerId] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Domain re-provision failed"),
  });

  const teardown = useMutation({
    mutationFn: () => api.admin.tearDownContainer(containerId),
    onSuccess: () => {
      toast.success(`Tore down ${data?.name ?? "container"}`);
      queryClient.invalidateQueries({ queryKey: ["admin-infrastructure"] });
      navigate({ to: "/superadmin/infrastructure" });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Teardown failed"),
  });

  const isBrain = data?.agentType === "boo-brain";
  const selfHosted = data?.hostingTarget === SELF_HOSTED;

  const { data: currentImage, isLoading: imageLoading } = useQuery({
    queryKey: ["admin-container-image", containerId],
    queryFn: () => api.admin.containerImage(containerId),
    enabled: !!containerId && isBrain,
  });
  const { data: versionsData, isLoading: versionsLoading } = useQuery({
    queryKey: ["admin-brain-versions"],
    queryFn: () => api.admin.brainVersions(),
    enabled: !!containerId && isBrain,
    staleTime: 5 * 60 * 1000,
  });
  const versions = versionsData?.versions ?? [];

  const runningImage = currentImage?.image ?? null;
  const isCurrentVersion = (v: BrainVersion) => v.image === runningImage;

  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [teardownOpen, setTeardownOpen] = useState(false);
  const { data: preflight, isLoading: preflightLoading } = useQuery({
    queryKey: ["admin-teardown-preflight", containerId],
    queryFn: () => api.admin.teardownPreflight(containerId),
    enabled: !!containerId && teardownOpen,
    staleTime: 30 * 1000,
  });

  const deployImage = useMutation({
    mutationFn: (deployTag: string) => api.admin.deployBrainImage(containerId, deployTag),
    onSuccess: (result) => {
      toast.success(`Deploying ${result.image} (${result.deploymentId.slice(0, 8)})`);
      setSelectedTag(null);
      queryClient.invalidateQueries({ queryKey: ["admin-container-image", containerId] });
      queryClient.invalidateQueries({ queryKey: ["admin-container", containerId] });
      queryClient.invalidateQueries({ queryKey: ["admin-infrastructure"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Deploy failed"),
  });

  const setChannel = useMutation({
    mutationFn: (channel: ReleaseChannel) => api.admin.setReleaseChannel(containerId, channel),
    onSuccess: (result) => {
      toast.success(
        result.releaseChannel === "pinned"
          ? "Pinned — the hourly rollout will leave this Boo alone"
          : `Now on the ${result.releaseChannel} channel — moves on the next hourly rollout`,
      );
      queryClient.invalidateQueries({ queryKey: ["admin-container-image", containerId] });
      queryClient.invalidateQueries({ queryKey: ["admin-container", containerId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Channel change failed"),
  });
  const releaseChannel = currentImage?.releaseChannel ?? data?.releaseChannel ?? "stable";

  const selectedVersion = versions.find((v) => v.tag === selectedTag) ?? null;
  // Concrete releases are immutable, so re-deploying the running one is a no-op
  // and blocked. `latest`/`edge` are moving pointers — always allow a re-roll.
  const selectedIsRunningRelease =
    !!selectedVersion && selectedVersion.kind === "release" && isCurrentVersion(selectedVersion);

  const railwayProjectUrl = data?.railwayProjectId
    ? `https://railway.com/project/${data.railwayProjectId}`
    : null;

  const domainProvisioning =
    data?.customDomainStatus === "pending" || data?.customDomainStatus === "verifying";

  // Tabs available for this container. Image/Models/Sandboxes are brain-only;
  // Bundles only when reached from the Named Warm Pool "Manage" flow (?bundles).
  const tabDefs = [
    { value: "overview", label: "Overview", show: true },
    { value: "image", label: "Image", show: isBrain && !selfHosted },
    { value: "models", label: "Models", show: isBrain },
    { value: "sandboxes", label: "Sandboxes", show: isBrain },
    { value: "feedback", label: "Feedback", show: isBrain },
    { value: "bundles", label: "Bundles", show: isBrain && showBundles },
  ].filter((t) => t.show);
  const activeTab = tab && tabDefs.some((t) => t.value === tab) ? tab : "overview";

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <Link
        to="/superadmin/infrastructure"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Infrastructure
      </Link>

      {isLoading ? (
        <LoadingIndicator />
      ) : isError || !data ? (
        <ErrorComponent error={error ?? "Failed to load container"} />
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold">{data.name}</h1>
              <StatusBadge status={data.deploymentStatus} errorMessage={data.errorMessage} />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                asChild
                title="Open this container's dashboard in a new tab (as site admin)"
              >
                <a
                  href={`/orgs/${data.organizationId}/containers/${data.id}/chat`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="size-3.5 mr-1.5" />
                  Open dashboard
                </a>
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={teardown.isPending}
                onClick={() => setTeardownOpen(true)}
              >
                <Trash2 className="size-3.5 mr-1.5" />
                {teardown.isPending ? "Tearing down…" : "Tear down"}
              </Button>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              navigate({
                to: "/superadmin/infrastructure/$containerId",
                params: { containerId },
                // Preserve ?bundles; drop ?tab on Overview to keep the URL clean.
                search: (prev) => ({
                  ...prev,
                  tab: value === "overview" ? undefined : value,
                }),
                replace: true,
              })
            }
          >
            <div className="-mx-1 overflow-x-auto px-1">
              <TabsList variant="line">
                {tabDefs.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="overview" className="mt-4">
              <div className="grid gap-4 lg:grid-cols-2 items-start">
                <SectionCard title="Identity">
                  <Field label="ID" value={data.id} mono />
                  <Field label="Description" value={data.description} />
                  <Field
                    label="Organization"
                    value={
                      <Link
                        to="/superadmin/organizations/$orgId"
                        params={{ orgId: data.organizationId }}
                        className="text-primary underline underline-offset-4 inline-flex items-center gap-1"
                      >
                        {data.organizationName}
                        <ExternalLink className="size-3" />
                      </Link>
                    }
                  />
                  <Field label="Org slug" value={data.organizationSlug} mono />
                  <Field label="Status" value={data.status} />
                  <Field label="Deployment" value={data.deploymentStatus} />
                  <Field label="Error" value={data.errorMessage} />
                </SectionCard>

                {selfHosted ? (
                  <SectionCard title="Self-hosted">
                    <SelfHostedSection
                      containerId={data.id}
                      brainUrl={data.railwayServiceUrl}
                      queryKey={["admin-container", containerId]}
                    />
                  </SectionCard>
                ) : (
                  <SectionCard title="Railway">
                    <Field
                      label="Project"
                      value={
                        railwayProjectUrl && data.railwayProjectId ? (
                          <a
                            href={railwayProjectUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline underline-offset-4 inline-flex items-center gap-1 font-mono text-xs"
                          >
                            {data.railwayProjectId}
                            <ExternalLink className="size-3" />
                          </a>
                        ) : (
                          data.railwayProjectId
                        )
                      }
                    />
                    <Field label="Environment" value={data.railwayEnvironmentId} mono />
                    <Field label="Brain service" value={data.railwayServiceId} mono />
                    <Field
                      label="Service URL"
                      value={
                        data.railwayServiceUrl ? (
                          <a
                            href={`https://${data.railwayServiceUrl}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline underline-offset-4 inline-flex items-center gap-1"
                          >
                            {data.railwayServiceUrl}
                            <ExternalLink className="size-3" />
                          </a>
                        ) : null
                      }
                    />
                    <Field label="Brain volume" value={data.brainVolumeId} mono />
                    <Field label="Redis service" value={data.redisServiceId} mono />
                    <Field label="Redis volume" value={data.redisVolumeId} mono />
                    <Field label="Home volume" value={data.homeVolumeId} mono />
                    <Field label="Workspace volume" value={data.workspaceVolumeId} mono />
                  </SectionCard>
                )}

                {isBrain && !selfHosted && (
                  <SectionCard title="Sandbox provider">
                    <SandboxProviderSection container={data} />
                  </SectionCard>
                )}

                {isBrain && !selfHosted && (
                  <SectionCard title="Bundles Railway token">
                    <BundleRailwayTokenSection containerId={containerId} />
                  </SectionCard>
                )}

                <SectionCard
                  title="Custom domain"
                  action={
                    isBrain && data.railwayServiceId ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={reprovisionDomain.isPending || domainProvisioning}
                        onClick={() => reprovisionDomain.mutate()}
                        title="Release the domain and re-run provisioning (Railway + DNS + cert) at the same FQDN. Fixes a domain stuck in error after a transient provisioning failure."
                      >
                        <RefreshCw
                          className={`size-3.5 mr-1.5 ${
                            reprovisionDomain.isPending || domainProvisioning ? "animate-spin" : ""
                          }`}
                        />
                        {domainProvisioning ? "Provisioning…" : "Re-provision"}
                      </Button>
                    ) : undefined
                  }
                >
                  <Field
                    label="FQDN"
                    value={
                      data.customDomainFqdn ? (
                        <a
                          href={`https://${data.customDomainFqdn}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline underline-offset-4 inline-flex items-center gap-1"
                        >
                          {data.customDomainFqdn}
                          <ExternalLink className="size-3" />
                        </a>
                      ) : null
                    }
                  />
                  <Field label="Status" value={data.customDomainStatus} />
                  <Field label="Error" value={data.customDomainError} />
                  <Field label="Railway domain ID" value={data.railwayCustomDomainId} mono />
                  <Field label="Cloudflare DNS ID" value={data.cloudflareDnsRecordId} mono />
                </SectionCard>

                <SectionCard title="Integrations">
                  <Field label="Gateway credential" value={data.gatewayCredentialId} mono />
                  <Field label="Slack team" value={data.slackTeamId} mono />
                  <Field label="Slack app" value={data.slackAppId} mono />
                  <Field label="Teams tenant" value={data.teamsTenantId} mono />
                  <Field label="Teams bot" value={data.teamsBotId} mono />
                  <Field label="Teams service URL" value={data.teamsServiceUrl} />
                </SectionCard>

                <SectionCard title="Billing">
                  <Field label="Currency" value={data.currency} />
                  <Field label="Manifest version" value={data.manifestVersion} mono />
                </SectionCard>

                {data.setup && (
                  <SectionCard title="Setup">
                    <SetupChecklist conditions={data.setup.conditions} showDetails />
                    <Field
                      label="Config"
                      value={`${data.setup.config.state}${data.setup.config.version ? ` · v${data.setup.config.reportedVersion ?? "–"} of v${data.setup.config.version}` : ""}${data.setup.config.failing.length ? ` · failing: ${data.setup.config.failing.join(", ")}` : ""}`}
                      mono
                    />
                    <Field
                      label="Waiting events"
                      value={data.setup.pendingEvents.length ? data.setup.pendingEvents.map((e) => `${e.kind}${e.attempts ? ` (${e.attempts} tries${e.lastError ? `: ${e.lastError}` : ""})` : ""}`).join(", ") : null}
                      mono
                    />
                    <Field label="Next check" value={untilIso(data.setup.nextRunAt)} />
                    <Field label="Last error" value={data.setup.lastError} mono />
                  </SectionCard>
                )}

                <SectionCard title="Timestamps">
                  <Field label="Created" value={relativeIso(data.createdAt)} />
                  <Field label="Updated" value={relativeIso(data.updatedAt)} />
                  <Field label="Claimed" value={data.claimedAt ? relativeIso(data.claimedAt) : null} />
                  <Field label="Deleted" value={data.deletedAt ? relativeIso(data.deletedAt) : null} />
                </SectionCard>
              </div>
            </TabsContent>

            {isBrain && (
              <TabsContent value="image" className="mt-4">
                <SectionCard
                  title="Brain image"
                  action={
                    currentImage?.image ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        {currentImage.image.split(":").pop()}
                      </span>
                    ) : undefined
                  }
                >
                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start py-1.5">
                    <div className="text-xs text-muted-foreground pt-0.5">Currently running</div>
                    <div>
                      {imageLoading ? (
                        <Skeleton className="h-4 w-56" />
                      ) : currentImage?.image ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs break-all">{currentImage.image}</span>
                          {currentImage.latestDeploymentStatus && (
                            <Badge variant="outline" className="text-[10px]">
                              {currentImage.latestDeploymentStatus}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic text-sm">—</span>
                      )}
                      {!imageLoading && currentImage && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {currentImage.running ? (
                            <>
                              Reports <span className="font-mono">{currentImage.running.version}</span> (
                              <span className="font-mono">{currentImage.running.gitSha}</span>)
                              {Number.isFinite(Date.parse(currentImage.running.builtAt)) &&
                                `, built ${relativeIso(currentImage.running.builtAt)}`}
                            </>
                          ) : (
                            "Not answering /version right now."
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start py-1.5">
                    <div className="text-xs text-muted-foreground pt-2">Release channel</div>
                    <RadioGroup
                      value={releaseChannel}
                      onValueChange={(v) => setChannel.mutate(v as ReleaseChannel)}
                      disabled={setChannel.isPending}
                      className="gap-2 sm:grid-cols-3 sm:grid"
                    >
                      {CHANNEL_OPTIONS.map((o) => (
                        <ChannelOption key={o.value} option={o} selected={releaseChannel === o.value} />
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="grid grid-cols-[140px_1fr] gap-3 items-start py-1.5">
                    <div className="text-xs text-muted-foreground pt-2">Switch version</div>
                    <div className="space-y-3">
                      {versionsLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      ) : versions.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No versions available.</p>
                      ) : (
                        <RadioGroup value={selectedTag ?? ""} onValueChange={setSelectedTag} className="gap-2 sm:grid-cols-2 sm:grid">
                          {versions.map((v) => (
                            <VersionOption
                              key={v.tag}
                              version={v}
                              selected={selectedTag === v.tag}
                              isCurrent={isCurrentVersion(v)}
                            />
                          ))}
                        </RadioGroup>
                      )}

                      <div className="flex items-center gap-3 pt-1">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              disabled={deployImage.isPending || !selectedVersion || selectedIsRunningRelease}
                            >
                              <Rocket className="size-3.5 mr-1.5" />
                              {deployImage.isPending ? "Deploying…" : "Deploy"}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Deploy {selectedVersion?.tag} to {data.name}?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Repoints the Railway service to{" "}
                                <span className="font-mono break-all">{selectedVersion?.image}</span> and
                                redeploys. Boo restarts on the new version and any in-flight work is
                                interrupted. Its volume, Redis, and settings are untouched. The release
                                channel switches to Pinned so the hourly rollout keeps this choice.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => selectedTag && deployImage.mutate(selectedTag)}>
                                Deploy {selectedVersion?.tag}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        {selectedIsRunningRelease && (
                          <span className="text-xs text-muted-foreground">Already running this version.</span>
                        )}
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </TabsContent>
            )}

            {isBrain && (
              <TabsContent value="models" className="mt-4">
                {data.deploymentStatus === "running" ? (
                  <ModelSettingsSection key={data.id} containerId={data.id} />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Model settings are available while the Boo is running.
                  </p>
                )}
              </TabsContent>
            )}

            {isBrain && (
              <TabsContent value="sandboxes" className="mt-4">
                {data.sandboxProvider === "vercel" ? (
                  <VercelSandboxesPanel containerId={data.id} />
                ) : (
                  <SandboxesPanel containerId={data.id} />
                )}
              </TabsContent>
            )}

            {isBrain && (
              <TabsContent value="feedback" className="mt-4">
                {data.deploymentStatus === "running" ? (
                  <ContainerFeedbackPanel containerId={data.id} />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Feedback reports are available while the Boo is running.
                  </p>
                )}
              </TabsContent>
            )}

            {isBrain && showBundles && (
              <TabsContent value="bundles" className="mt-4">
                <Bundles containerId={data.id} admin />
              </TabsContent>
            )}
          </Tabs>

          <ConfirmDialog
            open={teardownOpen}
            onOpenChange={setTeardownOpen}
            title={`Tear down ${data.name}?`}
            description="Tears down the Railway infrastructure, releases the custom domain, and revokes the gateway credential. Its record is soft-deleted — kept for billing history, not dropped. This can't be undone."
            confirmLabel="Tear down"
            confirmPhrase={data.name}
            loading={teardown.isPending}
            onConfirm={() => teardown.mutate()}
          >
            {preflightLoading ? (
              <p className="text-sm text-muted-foreground">Loading what will be deleted…</p>
            ) : preflight ? (
              <>
                {preflight.willDelete.length > 0 && (
                  <div className="rounded-md border bg-muted/40 p-3 text-sm">
                    <p className="mb-2 font-medium">
                      Tears down {preflight.willDelete.length} resource
                      {preflight.willDelete.length === 1 ? "" : "s"}
                    </p>
                    <ul className="max-h-56 space-y-1.5 overflow-y-auto">
                      {preflight.willDelete.map((r, i) => (
                        <li key={i} className="min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-muted-foreground/60">•</span>
                            <span>{r.label}</span>
                          </div>
                          {r.url ? (
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-4 block break-all font-mono text-xs text-primary underline underline-offset-2 hover:opacity-80"
                            >
                              {r.id}
                            </a>
                          ) : (
                            <div className="ml-4 break-all font-mono text-xs text-muted-foreground">
                              {r.id}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {preflight.manual && preflight.manual.length > 0 && (
                  <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                    <p className="mb-2 font-medium text-amber-700 dark:text-amber-500">
                      Teardown will NOT remove these — delete them manually
                    </p>
                    <ul className="space-y-2">
                      {preflight.manual.map((m, i) => (
                        <li key={i} className="min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-amber-600/70">•</span>
                            <span>
                              {m.type} <span className="text-muted-foreground">{m.detail}</span>
                            </span>
                          </div>
                          <div className="ml-4 text-xs text-muted-foreground">{m.whereToDelete}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : null}
          </ConfirmDialog>
        </>
      )}
    </div>
  );
}
